import React, { useMemo, useRef, useLayoutEffect, useState } from 'react';
import ManCrossPageFooter from '../components/ManCrossPageFooter';
import { formatRounded, computeIvcLevel } from '../shared/managerialUtils';

// ── IVC level config ─────────────────────────────────────────────────────────
// Nivel 3: IPRA<65 AND IASH<60 AND NPS<0  → Riesgo Crítico  #E74C3C
// Nivel 2: IPRA<65 AND IASH<60            → Riesgo Alto     #E67E22
// Nivel 1: IPRA<70 OR  IASH<65            → Riesgo Moderado #D4AC0D  (V4 §3.3)
// Sin IVC: resto                          → Riesgo Bajo     #62C190
// NOTE: NPS per-area not yet available; max level shown is Nivel 2.
const IVC_CONFIG = {
    3: { label: 'Riesgo Crítico',  shortLabel: 'Crítico',  color: '#E74C3C', textColor: '#fff'    },
    2: { label: 'Riesgo Alto',     shortLabel: 'Alto',     color: '#E67E22', textColor: '#fff'    },
    1: { label: 'Riesgo Moderado', shortLabel: 'Moderado', color: '#D4AC0D', textColor: '#1a1a1a' },
    0: { label: 'Riesgo Bajo',     shortLabel: 'Bajo',     color: '#62C190', textColor: '#fff'    },
};


// ── Action label per IVC level ────────────────────────────────────────────────
function getActionLabel(ivcLevel) {
    if (ivcLevel >= 2) return 'Esta semana';
    if (ivcLevel === 1) return '30 días';
    return 'Monitoreo';
}

// ── Algoritmo squarified (estilo D3) ─────────────────────────────────────────
// Rellena el contenedor 100% y produce bloques tan cuadrados como es posible
// dados los datos. Con escala cúbica los aspect-ratios quedan ≤ 2:1 para la
// mayoría de bloques, lo que visualmente se lee como "cuadraditos".
const _GAP = 5;

function _squarify(items, x0, y0, x1, y1, out) {
    if (!items.length) return;
    const dx = x1 - x0, dy = y1 - y0;
    if (items.length === 1) {
        out.push({ area: items[0].area, x: x0, y: y0, w: dx, h: dy });
        return;
    }
    const horizontal = dx >= dy;
    const side       = horizontal ? dy : dx;  // dimensión perpendicular al strip
    const totalSize  = items.reduce((s, i) => s + i.size, 0);

    // Encontrar el número óptimo de items en el strip (minimiza peor aspect-ratio)
    let bestN = 1, bestWorst = Infinity, accSize = 0;
    for (let i = 0; i < items.length; i++) {
        accSize += items[i].size;
        const stripW = (accSize / totalSize) * (horizontal ? dx : dy);
        let worst = 0, acc2 = 0;
        for (let j = 0; j <= i; j++) {
            acc2 += items[j].size;
            const bLen = (items[j].size / accSize) * side;
            worst = Math.max(worst, stripW / bLen, bLen / stripW);
        }
        if (worst <= bestWorst) { bestWorst = worst; bestN = i + 1; }
        else break;
    }

    const chosen     = items.slice(0, bestN);
    const chosenSize = chosen.reduce((s, i) => s + i.size, 0);
    const stripW     = (chosenSize / totalSize) * (horizontal ? dx : dy);

    let pos = horizontal ? y0 : x0;
    for (const item of chosen) {
        const bLen = (item.size / chosenSize) * side;
        out.push({
            area: item.area,
            x: horizontal ? x0        : pos,
            y: horizontal ? pos        : y0,
            w: horizontal ? stripW     : bLen,
            h: horizontal ? bLen       : stripW,
        });
        pos += bLen;
    }

    _squarify(
        items.slice(bestN),
        horizontal ? x0 + stripW : x0,
        horizontal ? y0          : y0 + stripW,
        x1, y1, out
    );
}

function squarifiedLayout(areas, containerW, containerH) {
    if (!areas || areas.length === 0) return [];
    const sorted = [...areas].sort((a, b) => (b.n || 0) - (a.n || 0));

    // Escala cúbica: side ∝ n^(1/3) → área visual ∝ n^(2/3), mejor balance
    const cbrtVals   = sorted.map(a => Math.cbrt(Math.max(a.n || 1, 1)));
    const total      = cbrtVals.reduce((s, v) => s + v, 0);
    const totalPxArea = containerW * containerH;

    const items = sorted.map((area, i) => ({
        area,
        size: (cbrtVals[i] / total) * totalPxArea,
    }));

    const raw = [];
    _squarify(items, 0, 0, containerW, containerH, raw);

    // Aplicar gap visual como inset en cada bloque
    return raw.map(r => ({
        area: r.area,
        x: r.x + _GAP / 2,
        y: r.y + _GAP / 2,
        w: Math.max(4, r.w - _GAP),
        h: Math.max(4, r.h - _GAP),
    }));
}

// ── SquareGrid component ──────────────────────────────────────────────────────
const SquareGrid = ({ data }) => {
    const containerRef = useRef(null);
    const [dims, setDims] = useState({ w: 0, h: 0 });

    useLayoutEffect(() => {
        if (!containerRef.current) return;
        const el = containerRef.current;
        const measure = () => {
            const { offsetWidth, offsetHeight } = el;
            if (offsetWidth > 0 && offsetHeight > 0) {
                setDims(prev =>
                    prev.w === offsetWidth && prev.h === offsetHeight
                        ? prev
                        : { w: offsetWidth, h: offsetHeight }
                );
            }
        };
        measure();
        const ro = new ResizeObserver(measure);
        ro.observe(el);
        return () => ro.disconnect();
    }, []);

    const layout = useMemo(
        () => squarifiedLayout(data, dims.w, dims.h),
        [data, dims.w, dims.h]
    );

    return (
        <div ref={containerRef} style={{ position: 'relative', width: '100%', height: '100%', overflow: 'hidden' }}>
            {layout.map(({ area, x, y, w, h }) => {
                const cfg     = IVC_CONFIG[area.ivcLevel] ?? IVC_CONFIG[0];
                const minDim  = Math.min(w, h);
                const showText = minDim >= 36;
                const showMeta = minDim >= 55;
                const fsName  = Math.max(8,  Math.min(11, Math.round(minDim * 0.09)));
                const fsIcp   = Math.max(12, Math.min(24, Math.round(minDim * 0.15)));
                const fsN     = Math.max(7,  Math.min(9,  Math.round(minDim * 0.07)));
                return (
                    <div
                        key={area.name}
                        style={{
                            position:       'absolute',
                            left:           Math.round(x),
                            top:            Math.round(y),
                            width:          Math.round(w),
                            height:         Math.round(h),
                            background:     cfg.color,
                            borderRadius:   6,
                            display:        'flex',
                            flexDirection:  'column',
                            alignItems:     'center',
                            justifyContent: 'center',
                            color:          cfg.textColor,
                            textAlign:      'center',
                            padding:        4,
                            overflow:       'hidden',
                            boxSizing:      'border-box',
                        }}
                    >
                        {showText && (
                            <div style={{ fontSize: fsName, fontWeight: 700, lineHeight: 1.2, marginBottom: 3 }}>
                                {area.name}
                            </div>
                        )}
                        {showText && (
                            <div style={{ fontSize: fsIcp, fontWeight: 900, lineHeight: 1 }}>
                                {area.icp ?? '–'}
                            </div>
                        )}
                        {showMeta && (
                            <div style={{ fontSize: 8, opacity: 0.75, marginTop: 2 }}>ICP</div>
                        )}
                        {showMeta && area.n > 0 && (
                            <div style={{ fontSize: fsN, opacity: 0.75, marginTop: 3 }}>
                                {`${area.n} pers.`}
                            </div>
                        )}
                    </div>
                );
            })}
        </div>
    );
};

// ── Main component ────────────────────────────────────────────────────────────
const ManCrossPageRiskMap = ({ reportData, orgName, headerText, pageNumber }) => {
    const payload    = reportData?.payload || {};
    const iashTotals = payload?.totals?.iash || {};
    const ipraTotals = payload?.totals?.ipra || {};
    const kpis       = payload?.kpis || {};

    // Headcount real por management_area (del payload — todos los activos, no solo evaluados)
    const headcountMap = useMemo(() => {
        const map = {};
        (payload?.headcount_by_management_area || []).forEach(e => {
            map[e.name] = e.headcount;
        });
        return map;
    }, [payload]);

    // Build per-area data joining IASH + IPRA averages (por management_area)
    const areas = useMemo(() => {
        const iashMap = {};
        (iashTotals?.average_by_management_area || []).forEach(a => {
            iashMap[a.name] = { avg: a.average };
        });
        const ipraMap = {};
        (ipraTotals?.average_by_management_area || []).forEach(a => {
            ipraMap[a.name] = { avg: a.average };
        });

        const names = new Set([...Object.keys(iashMap), ...Object.keys(ipraMap)]);
        return [...names]
            .map(name => {
                const iash   = iashMap[name]?.avg ?? null;
                const ipra   = ipraMap[name]?.avg ?? null;
                const n      = headcountMap[name] || 0;
                const icpRaw = (iash != null && ipra != null)
                    ? (iash + ipra) / 2
                    : (iash ?? ipra);
                const icp      = icpRaw != null ? Math.round(icpRaw) : null;
                const ivcLevel = computeIvcLevel(ipra, iash);
                return { name, iash, ipra, n, icp, ivcLevel };
            })
            .sort((a, b) => {
                if (b.ivcLevel !== a.ivcLevel) return b.ivcLevel - a.ivcLevel;
                return (a.icp ?? 999) - (b.icp ?? 999);
            });
    }, [iashTotals, ipraTotals, headcountMap]);

    // Total Planta summary row
    const totalRow = useMemo(() => {
        const ipra     = kpis.ipra_average ?? null;
        const iash     = kpis.iash_average ?? null;
        const icp      = kpis.cross_index  ?? null;
        const nps      = kpis.ipra_nps     ?? null;
        const n        = Object.values(headcountMap).reduce((s, v) => s + v, 0) || iashTotals.relationships_finished || 0;
        const ivcLevel = computeIvcLevel(ipra, iash);
        return { ipra, iash, icp, nps, n, ivcLevel };
    }, [kpis, iashTotals, headcountMap]);

    return (
        <div className="mgcr-riskmap-page">

            {/* ── Page header — hereda estilo estándar ── */}
            <div className="mgcr-page-header">
                <span className="mgcr-page-header-text">{headerText}</span>
                <span className="mgcr-page-header-num">{pageNumber}</span>
            </div>

            {/* ── Titles ── */}
            <div className="mgcr-riskmap-title-area">
                <h2 className="mgcr-riskmap-main-title">Mapa de Riesgo — IVC</h2>
                <p className="mgcr-riskmap-subtitle">¿Dónde está el riesgo hoy?</p>
            </div>

            {/* ── Body: treemap + executive table ── */}
            <div className="mgcr-riskmap-board">

                {/* LEFT 55% — Square grid, one block per management area */}
                <div className="mgcr-riskmap-treemap">
                    {areas.length > 0 ? (
                        <SquareGrid data={areas} />
                    ) : (
                        <div className="mgcr-riskmap-empty">Sin datos de área disponibles</div>
                    )}
                </div>

                {/* RIGHT 42% — Executive table */}
                <div className="mgcr-riskmap-table-wrap">
                    {/* Column headers */}
                    <div className="mgcr-riskmap-col-header">
                        <span>Área</span>
                        <span>N°</span>
                        <span>IPRA</span>
                        <span>IASH</span>
                        <span>NPS</span>
                        <span>ICP</span>
                        <span>IVC</span>
                        <span>Acción</span>
                    </div>

                    {/* Area rows — ordered by IVC desc, then ICP asc */}
                    {areas.map(a => {
                        const cfg = IVC_CONFIG[a.ivcLevel] ?? IVC_CONFIG[0];
                        return (
                            <div key={a.name} className="mgcr-riskmap-data-row">
                                <span className="mgcr-riskmap-area-cell">{a.name}</span>
                                <span>{a.n || '–'}</span>
                                <span>{formatRounded(a.ipra)}</span>
                                <span>{formatRounded(a.iash)}</span>
                                <span>–</span>
                                <span>{formatRounded(a.icp)}</span>
                                <span
                                    className="mgcr-riskmap-ivc-cell"
                                    style={{ background: cfg.color, color: cfg.textColor }}
                                >
                                    {cfg.shortLabel}
                                </span>
                                <span className="mgcr-riskmap-action-cell">
                                    {getActionLabel(a.ivcLevel)}
                                </span>
                            </div>
                        );
                    })}

                    {/* Total Planta — fondo #3E246B, texto blanco */}
                    {(() => {
                        const cfg = IVC_CONFIG[totalRow.ivcLevel] ?? IVC_CONFIG[0];
                        return (
                            <div className="mgcr-riskmap-total-row">
                                <span>Total Planta</span>
                                <span>{totalRow.n || '–'}</span>
                                <span>{formatRounded(totalRow.ipra)}</span>
                                <span>{formatRounded(totalRow.iash)}</span>
                                <span>{formatRounded(totalRow.nps, { signed: true })}</span>
                                <span>{formatRounded(totalRow.icp)}</span>
                                <span
                                    className="mgcr-riskmap-ivc-cell"
                                    style={{ background: cfg.color, color: cfg.textColor }}
                                >
                                    {cfg.shortLabel}
                                </span>
                                <span>{getActionLabel(totalRow.ivcLevel)}</span>
                            </div>
                        );
                    })()}
                </div>
            </div>

            {/* ── Footer teal bar (spec fixed text) ── */}
            <div className="mgcr-riskmap-insight-teal">
                Las áreas en rojo no necesitan monitoreo — necesitan intervención esta semana.
            </div>

            <ManCrossPageFooter />
        </div>
    );
};

export default ManCrossPageRiskMap;
